
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Users,
  UserPlus,
  Truck,
  MapPin,
  Settings,
  FileText,
  MessageSquare,
  Building,
  DollarSign,
  BarChart3,
  Map,
  Wrench,
  Route,
  Archive,
  Warehouse,
  Navigation,
  Calculator,
  CreditCard,
  Phone,
  History,
  Cog,
  MoreHorizontal
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar
} from '@/components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTranslation } from '@/lib/i18n';

// Cấu hình nhóm chức năng mới theo yêu cầu
const navigationGroups = [
  {
    label: 'Tổng quan',
    items: [
      { title: 'Báo cáo', href: '/reports', icon: BarChart3 },
      { title: 'Danh sách đơn hàng', href: '/orders', icon: Package },
      { title: 'Theo dõi đơn hàng', href: '/tracking', icon: MapPin }
    ]
  },
  {
    label: 'Hạ tầng & Vận chuyển',
    items: [
      { title: 'Bao trung chuyển', href: '/transit-bags', icon: Archive },
      { title: 'Nhập kho', href: '/warehouse', icon: Warehouse },
      { title: 'Vùng bao phủ', href: '/coverage', icon: Map },
      { title: 'Tuyến & Xe', href: '/trips', icon: Route },
      { title: 'Quản lý chuyến đi', href: '/trip-management', icon: Navigation },
      { title: 'Bưu cục', href: '/hubs', icon: Building },
      { title: 'Nhân viên', href: '/internal-accounts', icon: UserPlus },
    ]
  },
  {
    label: 'Dịch vụ & Báo cáo',
    items: [
      { title: 'Dịch vụ', href: '/services', icon: Wrench },
      { title: 'Cấu hình giá', href: '/price-configuration', icon: Calculator },
      { title: 'Thu COD', href: '/cod-collection', icon: CreditCard },
      { title: 'Đối soát', href: '/reconciliation', icon: DollarSign }
    ]
  },
  {
    label: 'Khác',
    items: [
      // { title: 'Dashboard', href: '/', icon: LayoutDashboard },
      { title: 'Khách hàng', href: '/customers', icon: Users }
    ]
  },
  {
    label: 'Hệ thống',
    items: [
      { title: 'Lịch sử cuộc gọi', href: '/call-history', icon: Phone },
      { title: 'Lịch sử hoạt động', href: '/activity-log', icon: History },
      { title: 'API 3PL', href: '/third-party-api', icon: Cog },
      { title: 'Hỗ trợ', href: '/support', icon: MessageSquare },
      { title: 'Cài đặt', href: '/settings', icon: Settings }
    ]
  }
];

export function AppSidebar() {
  const location = useLocation();
  const { state } = useSidebar();
  const { t } = useTranslation();

  return (
    <Sidebar className="border-r" collapsible="icon">
      {/* Container chính với custom scrollbar */}
      <div 
        className="h-full w-full overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/20 hover:scrollbar-thumb-white/30" 
        style={{ 
          background: 'var(--gradient-primary)',
          // Custom scrollbar styles cho webkit browsers
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent'
        }}
      >
        {/* Header với logo */}
        <SidebarHeader className={`p-4 ${state === 'collapsed' ? 'p-2' : 'bg-white'} border-b border-white/10`}>
          <div className="flex items-center space-x-2">
            {state === 'expanded' ? (
              // Hiển thị logo khi sidebar mở rộng
              <div className="flex items-center space-x-3">
                <img 
                  src="/assets/images/logo.png" 
                  alt="Logo" 
                  className="w-100 object-contain"
                  onError={(e) => {
                    // Fallback nếu logo không tải được
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling.style.display = 'flex';
                  }}
                />
                {/* Fallback icon nếu logo không load được */}
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg items-center justify-center hidden">
                  <span className="text-white font-bold text-sm">BX</span>
                </div>
              </div>
            ) : (
              // Hiển thị icon thu nhỏ khi sidebar thu gọn
              <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">BX</span>
              </div>
            )}
          </div>
        </SidebarHeader>
        
        {/* Nội dung navigation với spacing được tối ưu */}
        <SidebarContent className="overflow-y-auto">
          {navigationGroups.map((group, groupIndex) => (
            <SidebarGroup 
              key={group.label} 
              className={`${groupIndex > 0 ? 'mt-0' : 'mt-0'}`} // Giảm khoảng cách giữa các nhóm
            >
              {state === 'expanded' && (
                <SidebarGroupLabel className="text-white/70 dark:text-white/70 px-2 py-1 text-xs font-medium uppercase tracking-wider">
                  {group.label}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent className="mt-1"> {/* Giảm khoảng cách sau label */}
                <SidebarMenu className="space-y-1"> {/* Giảm khoảng cách giữa các menu item */}
                  {group.items.map((item) => (
                    <SidebarMenuItem key={item.href}>
                      {state === 'collapsed' ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <SidebarMenuButton 
                              asChild 
                              isActive={location.pathname === item.href}
                              className="text-white hover:bg-white/20 data-[active=true]:bg-white/30 dark:text-white dark:hover:bg-white/20 dark:data-[active=true]:bg-white/30 transition-colors duration-200 rounded-md"
                            >
                              <NavLink to={item.href} className="flex items-center gap-2 justify-center py-2">
                                <item.icon className="h-4 w-4" />
                              </NavLink>
                            </SidebarMenuButton>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="bg-background text-foreground border shadow-lg dark:bg-popover dark:text-popover-foreground">
                            <p>{item.title}</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <SidebarMenuButton 
                          asChild 
                          isActive={location.pathname === item.href}
                          className="text-white hover:bg-white/20 data-[active=true]:bg-white/30 dark:text-white dark:hover:bg-white/20 dark:data-[active=true]:bg-white/30 transition-colors duration-200 rounded-md"
                        >
                          <NavLink to={item.href} className="flex items-center gap-3 py-2 px-3">
                            <item.icon className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm font-medium">{item.title}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      )}
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>
      </div>
      
      {/* Custom CSS cho scrollbar - sử dụng CSS-in-JS thay vì styled-jsx */}
      <style dangerouslySetInnerHTML={{
        __html: `
          /* Webkit scrollbar styles */
          .scrollbar-thin::-webkit-scrollbar {
            width: 6px;
          }
          
          .scrollbar-thin::-webkit-scrollbar-track {
            background: transparent;
          }
          
          .scrollbar-thin::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.2);
            border-radius: 3px;
            transition: background 0.2s ease;
          }
          
          .scrollbar-thin::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.3);
          }
          
          /* Firefox scrollbar */
          .scrollbar-thin {
            scrollbar-width: thin;
            scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
          }
        `
      }} />
    </Sidebar>
  );
}
